import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, ExternalLink, Edit } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LAYOUT_TYPES = [
  { value: '2x1_horizontal', label: '2 w rzędzie [1][2]', rows: 1, cols: 2 },
  { value: '3x1_horizontal', label: '3 w rzędzie [1][2][3]', rows: 1, cols: 3 },
  { value: '4x1_horizontal', label: '4 w rzędzie [1][2][3][4]', rows: 1, cols: 4 },
  { value: '5x1_horizontal', label: '5 w rzędzie [1][2][3][4][5]', rows: 1, cols: 5 },
  { value: '6x1_horizontal', label: '6 w rzędzie [1][2][3][4][5][6]', rows: 1, cols: 6 },
  { value: '2x2_grid', label: '2x2 Grid', rows: 2, cols: 2 },
  { value: '3x2_grid', label: '3x2 Grid (3 góra, 3 dół)', rows: 2, cols: 3 },
  { value: '2x3_grid', label: '2x3 Grid (2 szerokości, 3 wysokości)', rows: 3, cols: 2 },
  { value: '3_2_asymmetric', label: '3+2 Asymetryczny', rows: 2, cols: 3, custom: '3_2' },
  { value: '2_3_asymmetric', label: '2+3 Asymetryczny', rows: 2, cols: 3, custom: '2_3' },
  { value: 'l_shape', label: 'L-Shape', rows: 3, cols: 3, custom: 'l' },
];

export default function ScreenGroups() {
  const { token, user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [screens, setScreens] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    layout_type: '4x1_horizontal',
    menu_display_time: 60,
    screen_assignments: {}
  });

  useEffect(() => {
    fetchGroups();
    fetchScreens();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/screen-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API_URL}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScreens(response.data);
    } catch (error) {
      console.error('Failed to fetch screens:', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    const selectedLayout = LAYOUT_TYPES.find(l => l.value === formData.layout_type);
    
    const groupData = {
      name: formData.name,
      layout_type: formData.layout_type,
      layout_config: {
        rows: selectedLayout.rows,
        cols: selectedLayout.cols,
        custom_positions: selectedLayout.custom ? [selectedLayout.custom] : null
      },
      menu_display_time: parseInt(formData.menu_display_time),
      screen_assignments: formData.screen_assignments
    };

    try {
      if (editingGroup) {
        await axios.put(`${API_URL}/screen-groups/${editingGroup.id}`, groupData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/screen-groups`, groupData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowCreateForm(false);
      setEditingGroup(null);
      setFormData({
        name: '',
        layout_type: '4x1_horizontal',
        menu_display_time: 60,
        screen_assignments: {}
      });
      fetchGroups();
    } catch (error) {
      console.error('Failed to save group:', error);
      alert('Błąd podczas zapisywania grupy');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę grupę?')) return;
    
    try {
      await axios.delete(`${API_URL}/screen-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
      alert('Błąd podczas usuwania grupy');
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      layout_type: group.layout_type,
      menu_display_time: group.menu_display_time,
      screen_assignments: group.screen_assignments || {}
    });
    setShowCreateForm(true);
  };

  const selectedLayout = LAYOUT_TYPES.find(l => l.value === formData.layout_type);
  const totalPositions = selectedLayout ? selectedLayout.rows * selectedLayout.cols : 0;

  const renderLayoutPreview = () => {
    if (!selectedLayout) return null;

    const { rows, cols, custom } = selectedLayout;
    
    if (custom === '3_2') {
      return (
        <div className="space-y-1">
          <div className="grid grid-cols-3 gap-1">
            {[1, 2, 3].map(i => (
              <div key={i} className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
                {i}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1">
            <div className="col-start-1 col-span-1"></div>
            {[4, 5].map(i => (
              <div key={i} className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
                {i}
              </div>
            ))}
          </div>
        </div>
      );
    } else if (custom === '2_3') {
      return (
        <div className="space-y-1">
          <div className="grid grid-cols-3 gap-1">
            <div className="col-start-1 col-span-1"></div>
            {[1, 2].map(i => (
              <div key={i} className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
                {i}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1">
            {[3, 4, 5].map(i => (
              <div key={i} className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
                {i}
              </div>
            ))}
          </div>
        </div>
      );
    } else if (custom === 'l') {
      return (
        <div className="grid grid-cols-3 gap-1">
          {[1, 2, 3].map(i => (
            <div key={i} className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
              {i}
            </div>
          ))}
          <div className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
            4
          </div>
          <div className="aspect-video"></div>
          <div className="aspect-video"></div>
          <div className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
            5
          </div>
        </div>
      );
    } else {
      // Standard grid
      return (
        <div className={`grid gap-1`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: rows * cols }).map((_, i) => (
            <div key={i} className="aspect-video bg-blue-100 border-2 border-blue-400 rounded flex items-center justify-center text-sm font-bold">
              {i + 1}
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Grupy Ekranów</h1>
        {user?.role === 'admin' && (
          <Button onClick={() => {
            setShowCreateForm(true);
            setEditingGroup(null);
            setFormData({
              name: '',
              layout_type: '4x1_horizontal',
              menu_display_time: 60,
              screen_assignments: {}
            });
          }}>
            <Plus className="mr-2 h-4 w-4" />
            Nowa Grupa
          </Button>
        )}
      </div>

      {showCreateForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{editingGroup ? 'Edytuj Grupę' : 'Utwórz Nową Grupę'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateGroup} className="space-y-4">
              <div>
                <Label htmlFor="name">Nazwa grupy</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="layout_type">Typ layoutu</Label>
                <Select
                  value={formData.layout_type}
                  onValueChange={(value) => setFormData({ ...formData, layout_type: value, screen_assignments: {} })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LAYOUT_TYPES.map(layout => (
                      <SelectItem key={layout.value} value={layout.value}>
                        {layout.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Podgląd layoutu</Label>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  {renderLayoutPreview()}
                </div>
              </div>

              <div>
                <Label htmlFor="menu_display_time">Czas wyświetlania menu (sekundy)</Label>
                <Input
                  id="menu_display_time"
                  type="number"
                  min="1"
                  value={formData.menu_display_time}
                  onChange={(e) => setFormData({ ...formData, menu_display_time: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Przypisanie ekranów do pozycji</Label>
                <div className="mt-2 space-y-2">
                  {Array.from({ length: totalPositions }).map((_, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <span className="w-full sm:w-24 text-sm font-medium">Pozycja {i + 1}:</span>
                      <Select
                        value={formData.screen_assignments[i + 1] || 'none'}
                        onValueChange={(value) => {
                          const newAssignments = { ...formData.screen_assignments };
                          if (value === 'none') {
                            delete newAssignments[i + 1];
                          } else {
                            newAssignments[i + 1] = value;
                          }
                          setFormData({
                            ...formData,
                            screen_assignments: newAssignments
                          });
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Wybierz ekran..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Brak</SelectItem>
                          {screens.map(screen => (
                            <SelectItem key={screen.id} value={screen.id}>
                              {screen.name} ({screen.resolution})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {editingGroup ? 'Zapisz zmiany' : 'Utwórz grupę'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingGroup(null);
                  }}
                >
                  Anuluj
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map(group => (
          <Card key={group.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{group.name}</span>
                {user?.role === 'admin' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditGroup(group)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteGroup(group.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">Layout:</span>{' '}
                  {LAYOUT_TYPES.find(l => l.value === group.layout_type)?.label}
                </p>
                <p>
                  <span className="font-medium">Czas menu:</span> {group.menu_display_time}s
                </p>
                <p>
                  <span className="font-medium">Ekranów:</span>{' '}
                  {Object.values(group.screen_assignments || {}).filter(v => v).length}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2 text-xs sm:text-sm"
                  onClick={() => window.open(`/display/group/${group.id}`, '_blank')}
                >
                  <ExternalLink className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Otwórz wyświetlacz</span>
                  <span className="sm:hidden">Otwórz</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {groups.length === 0 && !showCreateForm && (
        <div className="text-center py-12 text-gray-500">
          <p>Brak grup ekranów. Utwórz pierwszą grupę!</p>
        </div>
      )}
    </Layout>
  );
}
